import React, { useState, useEffect, useRef } from 'react';
import * as mobilenet from '@tensorflow-models/mobilenet';
import * as cocoSsd from '@tensorflow-models/coco-ssd';
import '@tensorflow/tfjs';

// Types
interface WasteInfo {
  category: string;
  bin: string;
  icon: string;
  color: string;
}

interface Detection extends WasteInfo {
  object: string;
  confidence: number;
}

// Waste Mapping
const wasteMapping: { [key: string]: WasteInfo } = {
  'bottle': { category: 'Plastic', bin: 'Recycling Bin (Blue)', icon: '♻️', color: '#2196F3' },
  'cup': { category: 'Plastic', bin: 'Recycling Bin (Blue)', icon: '♻️', color: '#2196F3' },
  'book': { category: 'Paper', bin: 'Recycling Bin (Blue)', icon: '📄', color: '#ADD8E6' },
  'newspaper': { category: 'Paper', bin: 'Recycling Bin (Blue)', icon: '📄', color: '#ADD8E6' },
  'can': { category: 'Metal', bin: 'Recycling Bin (Blue)', icon: '🥫', color: '#C0C0C0' },
  'apple': { category: 'Organic', bin: 'Compost Bin (Green)', icon: '🍎', color: '#4CAF50' },
  'banana': { category: 'Organic', bin: 'Compost Bin (Green)', icon: '🍌', color: '#4CAF50' },
  'orange': { category: 'Organic', bin: 'Compost Bin (Green)', icon: '🍊', color: '#4CAF50' },
  'broccoli': { category: 'Organic', bin: 'Compost Bin (Green)', icon: '🥦', color: '#4CAF50' },
  'carrot': { category: 'Organic', bin: 'Compost Bin (Green)', icon: '🥕', color: '#4CAF50' },
  'wine glass': { category: 'Glass', bin: 'Recycling Bin (Blue)', icon: '🍾', color: '#00FFFF' },
  'fork': { category: 'Metal', bin: 'Recycling Bin (Blue)', icon: '🍴', color: '#C0C0C0' },
  'knife': { category: 'Metal', bin: 'Recycling Bin (Blue)', icon: '🔪', color: '#C0C0C0' },
  'spoon': { category: 'Metal', bin: 'Recycling Bin (Blue)', icon: '🥄', color: '#C0C0C0' },
  'bowl': { category: 'General Waste', bin: 'Trash Bin (Black)', icon: '🗑️', color: '#808080' }
};

const App: React.FC = () => {
  const [mobileNetModel, setMobileNetModel] = useState<any>(null);
  const [cocoSsdModel, setCocoSsdModel] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [analyzing, setAnalyzing] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [error, setError] = useState<string>('');
  const [results, setResults] = useState<Detection[]>([]);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [cameraActive, setCameraActive] = useState<boolean>(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    loadModels();
    disableRightClick();
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  const disableRightClick = (): void => {
    document.addEventListener('contextmenu', e => e.preventDefault());
    document.addEventListener('keydown', e => {
      if (e.ctrlKey && (e.key === 'u' || e.key === 'U' || e.key === 's' || e.key === 'S' || (e.shiftKey && e.key === 'I'))) {
        e.preventDefault();
      }
      if (e.key === 'F12') {
        e.preventDefault();
      }
    });
    document.addEventListener('selectstart', e => e.preventDefault());
  };

  const loadModels = async (): Promise<void> => {
    try {
      setProgress(10);
      const mobilenet_model = await mobilenet.load();
      setMobileNetModel(mobilenet_model);
      setProgress(50);
      const coco_model = await cocoSsd.load();
      setCocoSsdModel(coco_model);
      setProgress(100);
      setTimeout(() => setLoading(false), 500);
    } catch (err) {
      console.error('Model loading error:', err);
      setError('Failed to load AI models. Please refresh.');
    }
  };

  const startCamera = async (): Promise<void> => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setCameraActive(true);
      setError('');
    } catch (err) {
      setError('Could not access camera. Please allow permissions or upload a photo.');
    }
  };

  const stopCamera = (): void => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setCameraActive(false);
  };

  const captureImage = (): void => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0);

    canvas.toBlob(blob => {
      if (blob) {
        analyzeImage(blob);
      }
    }, 'image/jpeg', 0.95);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) {
      analyzeImage(file);
    }
  };

  const analyzeImage = async (imageBlob: Blob): Promise<void> => {
    stopCamera();
    setAnalyzing(true);
    setError('');
    setResults([]);

    try {
      const img = await createImageElement(imageBlob);
      setPreviewImage(img.src);

      const [mobileNetPredictions, cocoSsdPredictions] = await Promise.all([
        mobileNetModel.classify(img),
        cocoSsdModel.detect(img)
      ]);

      const detections = processDetections(mobileNetPredictions, cocoSsdPredictions);

      if (detections.length > 0) {
        setResults(detections);
      } else {
        setError('Could not identify waste item. Please try with a clearer image.');
      }
    } catch (err) {
      console.error('Analysis error:', err);
      setError('Error analyzing image. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const createImageElement = (blob: Blob): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = URL.createObjectURL(blob);
    });
  };

  const processDetections = (mobileNetResults: any[], cocoSsdResults: any[]): Detection[] => {
    const detections: Detection[] = [];

    cocoSsdResults.forEach(prediction => {
      const className = prediction.class.toLowerCase();
      const confidence = prediction.score;

      if (confidence > 0.3) {
        const wasteInfo = classifyWaste(className);
        if (wasteInfo) {
          detections.push({
            object: prediction.class,
            confidence: confidence,
            ...wasteInfo
          });
        }
      }
    });

    if (detections.length === 0 && mobileNetResults.length > 0) {
      const topPrediction = mobileNetResults[0];
      const className = topPrediction.className.toLowerCase();
      const wasteInfo = classifyWaste(className);

      if (wasteInfo) {
        detections.push({
          object: topPrediction.className,
          confidence: topPrediction.probability,
          ...wasteInfo
        });
      }
    }

    return detections;
  };

  const classifyWaste = (detectedObject: string): WasteInfo | null => {
    if (wasteMapping[detectedObject]) {
      return wasteMapping[detectedObject];
    }

    for (const [key, value] of Object.entries(wasteMapping)) {
      if (detectedObject.includes(key) || key.includes(detectedObject)) {
        return value;
      }
    }

    if (detectedObject.includes('bottle') || detectedObject.includes('plastic')) {
      return { category: 'Plastic', bin: 'Recycling Bin (Blue)', icon: '♻️', color: '#2196F3' };
    }
    if (detectedObject.includes('paper') || detectedObject.includes('cardboard')) {
      return { category: 'Paper', bin: 'Recycling Bin (Blue)', icon: '📄', color: '#ADD8E6' };
    }
    if (detectedObject.includes('can') || detectedObject.includes('metal')) {
      return { category: 'Metal', bin: 'Recycling Bin (Blue)', icon: '🥫', color: '#C0C0C0' };
    }
    if (detectedObject.includes('food') || detectedObject.includes('fruit')) {
      return { category: 'Organic', bin: 'Compost Bin (Green)', icon: '🍎', color: '#4CAF50' };
    }

    return { category: 'General Waste', bin: 'Trash Bin (Black)', icon: '🗑️', color: '#808080' };
  };

  const resetApp = (): void => {
    setResults([]);
    setError('');
    setPreviewImage(null);
  };

  return (
    <div className="app">
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
          -webkit-user-select: none;
          -moz-user-select: none;
          user-select: none;
        }

        body {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          min-height: 100vh;
          padding: 20px;
          color: #333;
        }

        .app {
          width: 100%;
        }

        .container {
          max-width: 800px;
          margin: 0 auto;
        }

        .header {
          text-align: center;
          color: white;
          margin-bottom: 30px;
        }

        .header h1 {
          font-size: 2.5em;
          margin-bottom: 10px;
          text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
        }

        .header .badge {
          background: rgba(255, 255, 255, 0.2);
          padding: 8px 20px;
          border-radius: 20px;
          display: inline-block;
          margin-top: 10px;
        }

        .ai-status {
          display: inline-block;
          padding: 5px 15px;
          border-radius: 15px;
          font-size: 0.9em;
          margin-left: 10px;
        }

        .ai-status.ready {
          background: #4caf50;
          color: white;
        }

        .ai-status.loading {
          background: #ff9800;
          color: white;
        }

        .card {
          background: white;
          border-radius: 20px;
          padding: 30px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          margin-bottom: 20px;
        }

        .loading-model {
          text-align: center;
          padding: 40px;
        }

        .loading-model h3 {
          margin-bottom: 20px;
          color: #667eea;
        }

        .progress-bar {
          width: 100%;
          height: 30px;
          background: #e0e0e0;
          border-radius: 15px;
          overflow: hidden;
          margin-bottom: 15px;
        }

        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
          transition: width 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
        }

        .info-box {
          background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);
          padding: 20px;
          border-radius: 15px;
          margin-bottom: 20px;
        }

        .info-box h3 {
          margin-bottom: 15px;
          color: #333;
        }

        .info-box ol {
          margin-left: 20px;
          line-height: 1.8;
        }

        .camera-section {
          text-align: center;
        }

        video {
          width: 100%;
          max-width: 640px;
          border-radius: 15px;
          background: #000;
          margin-bottom: 20px;
        }

        .preview-image {
          max-width: 100%;
          border-radius: 15px;
          margin: 20px auto;
          display: block;
        }

        .btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          padding: 15px 30px;
          font-size: 1.1em;
          border-radius: 50px;
          cursor: pointer;
          margin: 10px;
          transition: transform 0.2s, box-shadow 0.2s;
          font-weight: bold;
        }

        .btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
        }

        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-secondary {
          background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
        }

        .btn-success {
          background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
        }

        .upload-label {
          display: inline-block;
          background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
          color: white;
          padding: 15px 30px;
          font-size: 1.1em;
          border-radius: 50px;
          cursor: pointer;
          margin: 10px;
          transition: transform 0.2s;
          font-weight: bold;
        }

        .upload-label:hover {
          transform: translateY(-2px);
        }

        .loading {
          text-align: center;
          padding: 20px;
        }

        .spinner {
          border: 4px solid #f3f3f3;
          border-top: 4px solid #667eea;
          border-radius: 50%;
          width: 50px;
          height: 50px;
          animation: spin 1s linear infinite;
          margin: 0 auto 15px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .error {
          background: #ff4444;
          color: white;
          padding: 15px;
          border-radius: 10px;
          margin-top: 20px;
        }

        .result-card {
          animation: slideIn 0.5s ease-out;
        }

        .result-card h2 {
          margin-bottom: 20px;
          text-align: center;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .detection-results {
          margin-bottom: 20px;
        }

        .detection-result {
          background: #f8f9fa;
          padding: 20px;
          border-radius: 10px;
          margin-bottom: 15px;
          border-left: 5px solid;
        }

        .detected-item {
          font-size: 1.5em;
          font-weight: bold;
          margin-bottom: 10px;
        }

        .confidence-score {
          color: #666;
          margin-bottom: 10px;
        }

        .bin-recommendation {
          font-size: 1.3em;
          font-weight: bold;
          padding: 15px;
          border-radius: 10px;
          margin: 20px 0;
          text-align: center;
        }

        .bin-icon {
          font-size: 3em;
          margin-bottom: 10px;
        }

        .bin-instruction {
          font-size: 1.1em;
          margin-top: 10px;
        }

        @media (max-width: 600px) {
          .header h1 {
            font-size: 2em;
          }

          .btn,
          .upload-label {
            padding: 12px 20px;
            font-size: 1em;
            margin: 5px;
          }

          .detected-item {
            font-size: 1.2em;
          }

          .bin-recommendation {
            font-size: 1.1em;
          }
        }
      `}</style>

      <div className="container">
        {/* Header */}
        <div className="header">
          <h1>Waste Detection</h1>
          <div className="badge">
            <span className={`ai-status ${loading ? 'loading' : 'ready'}`}>
              {loading ? 'Loading Model...' : 'Hello Mousam is Ready. Scan , Test my intellegence and Win.  ✓'}
            </span>
          </div>
        </div>

        {loading ? (
          /* Model Loading */
          <div className="card loading-model">
            <h3>🧠 Loading Model...</h3>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }}>
                {progress}%
              </div>
            </div>
            <p>Please wait while we load the neural network...</p>
          </div>
        ) : (
          <>
            {/* Info Box */}
            <div className="card info-box">
              <h3>📱 How It Works:</h3>
              <ol>
                <li>
                  <strong>Real AI Detection:</strong> Uses Intelligence from 50 thousands + dataset tested at level 43 offices
                </li>
                <li>
                  <strong>Object Recognition:</strong> Identifies actual objects (bottles, cans, paper, keyboard, mouse etc.)
                </li>
                <li>
                  <strong>Smart Classification:</strong> Maps detected objects to waste categories
                </li>
                <li>
                  <strong>Instant Results:</strong> Get bin recommendations in real-time!
                </li>
                <li>
                  <strong>Instant Point Calculation:</strong> Scan and throw, get points and win monthly!
                </li>
              </ol>
            </div>

            {/* Camera Section */}
            <div className="card">
              <div className="camera-section">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  style={{ display: cameraActive ? 'block' : 'none' }}
                />
                <canvas ref={canvasRef} style={{ display: 'none' }} />

                {previewImage && (
                  <img
                    src={previewImage}
                    alt="Preview"
                    className="preview-image"
                  />
                )}

                <div className="controls">
                  {!cameraActive ? (
                    <button className="btn" onClick={startCamera}>
                      📷 Open Camera
                    </button>
                  ) : (
                    <>
                      <button className="btn btn-secondary" onClick={captureImage}>
                        📸 Capture & Analyze
                      </button>
                      <button className="btn btn-success" onClick={stopCamera}>
                        ❌ Close Camera
                      </button>
                    </>
                  )}
                  <br />
                  <label htmlFor="fileInput" className="upload-label">
                    📁 Upload Photo
                  </label>
                  <input
                    type="file"
                    id="fileInput"
                    accept="image/*"
                    capture="environment"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                  />
                </div>

                {analyzing && (
                  <div className="loading">
                    <div className="spinner"></div>
                    <p>🤖 AI is analyzing waste...</p>
                  </div>
                )}

                {error && (
                  <div className="error">
                    ⚠️ {error}
                  </div>
                )}
              </div>
            </div>

            {/* Results */}
            {results.length > 0 && (
              <div className="card result-card">
                <h2>🎯 Detection Results</h2>
                <div className="detection-results">
                  {results.map((detection, index) => (
                    <div
                      key={index}
                      className="detection-result"
                      style={{ borderColor: detection.color }}
                    >
                      <div className="detected-item">
                        {detection.icon} Detected: {detection.object}
                      </div>
                      <div className="confidence-score">
                        AI Confidence: {(detection.confidence * 100).toFixed(1)}%
                      </div>
                      <div
                        className="bin-recommendation"
                        style={{
                          background: `${detection.color}33`,
                          color: detection.color
                        }}
                      >
                        <div className="bin-icon">{detection.icon}</div>
                        <div>Waste Type: <strong>{detection.category}</strong></div>
                        <div className="bin-instruction">
                          👉 Throw in: <strong>{detection.bin} and Grab a Point</strong>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <button className="btn" onClick={resetApp}>
                  🔄 Detect Another Item
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default App;